import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Car, Maximize, ArrowUpRight } from "lucide-react";
import { formatBRL } from "@/lib/scrollTo";
import { resolveImg } from "@/lib/img";

const PropertyCard = ({ property, className = "", index = 0 }) => {
  const navigate = useNavigate();
  return (
    <motion.article
      layout
      data-testid={`property-card-${property.slug}`}
      onClick={() => navigate(`/imoveis/${property.slug}`)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative cursor-pointer overflow-hidden border border-white/10 bg-coal transition-[border-color] duration-500 hover:border-brand/40 ${className}`}
    >
      <div className="relative h-full min-h-[380px] w-full overflow-hidden">
        <img
          src={resolveImg(property.imagens[0])}
          alt={property.titulo}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/25 to-transparent" />

        <div className="absolute left-5 top-5 flex gap-2">
          <span className="bg-brand px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
            {property.negocio}
          </span>
          <span className="border border-white/20 bg-obsidian/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-200 backdrop-blur-md">
            {property.tipo}
          </span>
          {property.destaque && (
            <span className="bg-sun px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-obsidian">
              Destaque
            </span>
          )}
        </div>

        <div className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 bg-obsidian/60 text-stone-200 backdrop-blur-md transition-colors duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-white">
          <ArrowUpRight size={18} />
        </div>

        <div className="absolute inset-x-0 bottom-0 p-6">
          <p className="font-serif text-2xl text-[#F4F4F6]" data-testid={`property-price-${property.slug}`}>
            {formatBRL(property.preco, property.negocio)}
          </p>
          <h3 className="mt-1 font-serif text-xl text-[#F4F4F6] sm:text-2xl">{property.titulo}</h3>
          <p className="mt-1 text-sm font-light text-stone-400">
            {property.bairro} — {property.cidade}
          </p>
          <div className="mt-4 flex items-center gap-5 border-t border-white/15 pt-4 text-xs text-stone-300">
            <span className="flex items-center gap-1.5">
              <Maximize size={13} className="text-brand" /> {property.area} m²
            </span>
            {property.quartos > 0 && (
              <span className="flex items-center gap-1.5">
                <BedDouble size={13} className="text-brand" /> {property.quartos}
              </span>
            )}
            {property.banheiros > 0 && (
              <span className="flex items-center gap-1.5">
                <Bath size={13} className="text-brand" /> {property.banheiros}
              </span>
            )}
            {property.vagas > 0 && (
              <span className="flex items-center gap-1.5">
                <Car size={13} className="text-brand" /> {property.vagas}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default PropertyCard;
